import { FC } from 'react';
import { TreeItem as TreeItemType } from './types';
import { TreeItemWithSubItems } from './TreeItemWithSubItems';

interface TreeListProps<T = any> {
  items: TreeItemType<T>[];
  level: 'main' | 'sub' | 'subSub';
  parentId?: string | number;
  grandParentId?: string | number;
  onToggleMain: (id: string | number) => void;
  onToggleSub: (mainId: string | number, subId: string | number) => void;
  onToggleSubSub: (mainId: string | number, subId: string | number, subSubId: string | number) => void;
  renderItem?: (item: TreeItemType<T>, level: 'main' | 'sub' | 'subSub') => React.ReactNode;
  isMultiSelect: boolean;
}

export const TreeList: FC<TreeListProps> = ({
  items,
  level,
  parentId,
  grandParentId,
  onToggleMain,
  onToggleSub,
  onToggleSubSub,
  renderItem,
  isMultiSelect,
}) => {
  // Calculate the selected count for an item
  const calculateSelectedCount = (item: TreeItemType): number => {
    if (level === 'subSub') {
      // For sub-sub items, just return 1 if selected, 0 otherwise
      return item.selected ? 1 : 0;
    }

    if (level === 'sub') {
      // For sub items, count self and all selected sub-sub items
      return (item.selected ? 1 : 0) + (item.subItems?.filter((subSub) => subSub.selected).length || 0);
    }

    // For main items, recursively count all selected items
    return (
      (item.selected ? 1 : 0) +
      (item.subItems?.reduce((count, sub) => count + (sub.selected ? 1 : 0) + (sub.subItems?.filter((subSub) => subSub.selected).length || 0), 0) || 0)
    );
  };

  return (
    <div className={`relative ${level === 'main' ? 'flex flex-col gap-8' : ''}`}>
      {items.map((item) => (
        <TreeItemWithSubItems
          key={item.id}
          item={item}
          level={level}
          parentId={parentId}
          grandParentId={grandParentId}
          onToggleMain={onToggleMain}
          onToggleSub={onToggleSub}
          onToggleSubSub={onToggleSubSub}
          calculateSelectedCount={calculateSelectedCount}
          renderItem={renderItem}
          isMultiSelect={isMultiSelect}
        />
      ))}
    </div>
  );
};
